import type { ReactNode } from "react";
import { observer } from "mobx-react-lite";
import type {
  ActionOutputData,
  OutputData,
  ValueData,
  RecordJobData,
} from "@smartsuite/automation-engine-api";
import { translateAction } from "../../../../../locales/i18n";
import { t } from "i18next";
import { ActionError } from "../ActionError/ActionError";
import { ActivityDetailsTriggerGroup } from "../ActivityDetailsTriggerGroup/ActivityDetailsTriggerGroup";
import { ActivityDetailsItem } from "../ActivityDetailsItem/ActivityDetailsItem";
import {
  addEmptyValueIfNeeded,
  getErrorMessage,
} from "../../../ActivityGrid/ActivityGrid.helpers";
import { ActivityDetailsGroup } from "../ActivityDetailsGroup/ActivityDetailsGroup";
import { getEditRecordUrl } from "../../utils";
import { useRootStore } from "../../../../../stores/hooks";
import { valueDataToStringsWithDelimenters } from "@smartsuite/automation-conditions";

interface MergeRecordsGroupProps {
  action: ActionOutputData;
  onDetailsClick?: () => void;
}

export const recordOutputIds = [
  "field-id",
  "record_id",
  "application_id",
  "form_name",
  "form_url",
  "title",
];

export const MergeRecordsGroup = observer<MergeRecordsGroupProps>(
  ({ action, onDetailsClick }) => {
    const { integrations, jobDetails } = useRootStore();
    const selectedIntegration = integrations.list.find(
      (i) => i.integrationId === action.actionReference?.integrationId,
    );
    const selectedAction = selectedIntegration?.actions.find(
      (a) => a.actionId === action.actionReference?.actionId,
    );
    const errorMessage = getErrorMessage(action, jobDetails.job);
    const status = action.status.oneofKind === "ok" ? "is_success" : "is_error";
    const filteredRecordJobDatas = jobDetails.recordJobData.filter(
      (recordJobData) => {
        return (
          recordJobData.outputData?.recordList?.recordList !== undefined &&
          recordJobData.outputData.recordList.recordList?.records.length > 0 &&
          recordJobData.outputData.recordList.recordList.records[0]?.outputs.some(
            (output) => output.outputId === "merged-record-id",
          )
        );
      },
    );

    return (
      <ActivityDetailsGroup
        label={
          selectedAction !== undefined ? translateAction(selectedAction) : ""
        }
        subLabel={t("job-list.records-found", {
          count: filteredRecordJobDatas.length,
        })}
        status={status}
      >
        {status === "is_error" && <ActionError errorMessage={errorMessage} />}
        {filteredRecordJobDatas.map((recordJob, index) => {
          const outputs =
            recordJob.outputData !== undefined
              ? getRecordListOutputs(recordJob.outputData)
              : [];

          // do not show entire fields
          const withoutEnterFields =
            filterEntireFieldsFromRecordJobData(outputs);

          const filteredOutputs = withoutEnterFields.filter(
            (output) => !recordOutputIds.includes(output.outputId),
          );

          const mergedAndDeletedRecord = findOutputValueAndLabel(
            "merged-record-id",
            outputs,
          );

          const applicationId = findOutputValueAndLabel(
            "application_id",
            outputs,
          );
          const solutionId = findOutputValueAndLabel("solution_id", outputs);
          const mergedToRecord = findOutputValueAndLabel("record_id", outputs);
          const fieldsToDisplay = getMergedFields(recordJob);

          return (
            <ActivityDetailsTriggerGroup
              key={index}
              title={`Merge Result ${index + 1}`}
              onClick={onDetailsClick}
            >
              <ActivityDetailsItem label={"Merged Record Id (Deleted Record)"}>
                {getLinkToRecord(
                  mergedAndDeletedRecord,
                  solutionId,
                  applicationId,
                )}
              </ActivityDetailsItem>
              <ActivityDetailsItem label={"Merged To Record Id"}>
                {getLinkToRecord(mergedToRecord, solutionId, applicationId)}
              </ActivityDetailsItem>
              {filteredOutputs
                .filter((output) => output.outputId !== "merged-record-id")
                .filter((output) =>
                  fieldsToDisplay.includes(
                    output.outputId.replace("field-", "").split(".")[0],
                  ),
                )
                .map((output) => {
                  return (
                    <ActivityDetailsItem
                      key={output.outputId}
                      label={output.outputLabel}
                    >
                      {addEmptyValueIfNeeded(getOutputValue(output.value))}
                    </ActivityDetailsItem>
                  );
                })}
            </ActivityDetailsTriggerGroup>
          );
        })}
      </ActivityDetailsGroup>
    );
  },
);

const findOutputValueAndLabel = (
  outputId: string,
  outputs: OutputData[],
): string | undefined => {
  const output = outputs.find((output) => output.outputId === outputId);
  if (output === undefined) {
    return undefined;
  }

  return getOutputValue(output.value);
};

const getRecordListOutputs = (outputData: ActionOutputData): OutputData[] => {
  if (outputData.recordList?.recordList === undefined) {
    return [];
  }
  return outputData.recordList.recordList.records[0]?.outputs;
};

const getOutputValue = (value: ValueData | undefined): string => {
  return valueDataToStringsWithDelimenters(value).join("; ");
};

const getLinkToRecord = (
  recordId?: string,
  solutionId?: string,
  applicationId?: string,
): ReactNode => {
  if (recordId === undefined) {
    return <></>;
  }
  return (
    <a
      href={getEditRecordUrl(recordId, solutionId, applicationId)}
      target="_blank"
      rel="noreferrer"
    >
      {recordId}
    </a>
  );
};

const filterEntireFieldsFromRecordJobData = (
  outputs: OutputData[],
): OutputData[] => {
  return outputs.filter((output) => {
    //   filter the same field
    if (
      outputs.some((output2) => "field-" + output.outputId === output2.outputId)
    ) {
      return false;
    }
    // filter entire field
    if (!isFieldWithSuffix(output)) {
      const isExistWithSuffix = outputs.some((output2) => {
        return (
          output2.outputId.includes(output.outputId) &&
          isFieldWithSuffix(output2)
        );
      });
      return !isExistWithSuffix;
    }
    return true;
  });
};

const isFieldWithSuffix = (input: OutputData): boolean => {
  return input.outputId.includes(".");
};

const getMergedFields = (recordJob: RecordJobData): string[] => {
  const mergedFieldsInput = recordJob.inputs.find(
    (input) => input.inputId === "merge-actions",
  );
  if (mergedFieldsInput === undefined) {
    return [];
  }
  const input = mergedFieldsInput.value?.values.find(
    (item) => item.value.oneofKind === "mergeFieldActions",
  );
  if (input === undefined) {
    return [];
  }
  if (input.value.oneofKind !== "mergeFieldActions") {
    return [];
  }
  const fields = input.value.mergeFieldActions.fields.map(
    (field) => field.fieldSlug,
  );
  return fields;
};
